import React from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import { faHandshakeAngle } from '@fortawesome/pro-regular-svg-icons/faHandshakeAngle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dropdown from 'components/cosmos/dropdown';
import { ReactComponent as BadgeDollar } from 'components/svg/icons/badge-dollar-lg.svg';
import { ReactComponent as FileAlt } from 'components/svg/icons/file-alt-lg.svg';
import { ReactComponent as QuestionSquare } from 'components/svg/icons/question-square-lg.svg';
import { ReactComponent as SignalStream } from 'components/svg/icons/signal-stream-lg.svg';
import Tooltip from 'components/tooltip/Tooltip';
import { I18n } from 'components/translate';
import { Badge } from 'components/v2/badge';
import {
  MODULE_CONTENT,
  MODULE_QUIZ,
  MODULE_ADVERTISEMENT,
  MODULE_WEBINAR,
  ROUTES
} from 'configs/routes/constants';
import { showSurvey, SURVEY_ID_LIST } from 'configs/Survicate';
import { inject, PropTypes as MobxPropTypes } from 'mobx-react';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import {
  DatahubEventsService,
  DatahubViewCreateMentorshipService
} from 'modules/app/services/datahubEvents';
import ContentService from 'modules/content/ContentService';
import { FeatureToggles } from 'modules/featureToggle';
import DisabledOnOnlineEvent from 'modules/onlineEvent/components/DisabledOnOnlineEvent';
import PropTypes from 'prop-types';

import ContentEvents from '../../events';
import { clickCreate } from '../webinar/LiveEvents';

import './ContentCreateActions.scss';

const ContentCreateActions = ({
  itemId,
  isExtra,
  selectedSeason,
  onboarding,
  history,
  urlWithParams,
  contentNewURL,
  isOnlineEvent = false,
  liveMfeToggle,
  onCreateContentEvents,
  isMobile,
  isEnabledMentorship
}) => {
  const isNeedPulsedContentButton = () =>
    onboarding.showVersionWithOnboarding &&
    onboarding.checkList.hasModule &&
    !onboarding.checkList.hasContent;

  const getNewLiveUrl = (itemId, itemType, isExtra, selectedSeason) => {
    if (liveMfeToggle.value) {
      const url = ROUTES.CONTENT.NEW_LIVE_MF.replace(
        ':moduleId',
        itemId
      ).replace('/', '');

      return urlWithParams(url, { extra: isExtra, season: selectedSeason });
    }

    return contentNewURL({ itemId, itemType, isExtra, selectedSeason });
  };

  const renderContenButtonAction = (button) => {
    if (isOnlineEvent && isNeedPulsedContentButton()) {
      return (
        <Tooltip
          text="onboarding.online_event.new_create_info_page"
          position="top"
        >
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  const handleAdsButtonClick = () => {
    onCreateContentEvents(EVENTS_LABELS.CONTENT.MANAGE_MODULE.ADS);
  };

  const onCreateWebinar = (e) => {
    onCreateContentEvents(EVENTS_LABELS.CONTENT.MANAGE_MODULE.LIVE);

    clickCreate && clickCreate(e);
  };

  const renderWebinarButtonAction = () => {
    const url = getNewLiveUrl(itemId, MODULE_WEBINAR);

    const handleButtonClick = () => {
      liveMfeToggle.value && history.push(url);
    };

    let button = (
      <button
        className="_d-flex _justify-content-between _align-items-center _px-5 _py-4 _w-full"
        onClick={handleButtonClick}
      >
        <div className="_d-flex _align-items-center">
          <SignalStream className="content-create-actions__icon" />
          <p className="_mb-0 _mt-0 _ml-4 _text-2 _text-gray-700">
            <I18n label="general.webinar" />
          </p>
        </div>
      </button>
    );

    if (isOnlineEvent && isNeedPulsedContentButton()) {
      button = (
        <Tooltip
          text="onboarding.online_event.create_transmission_page"
          position="top"
        >
          {button}
        </Tooltip>
      );
    }

    if (!liveMfeToggle.value) {
      button = (
        <Link to={url} className="draggable-link" onClick={onCreateWebinar}>
          {button}
        </Link>
      );
    }

    return button;
  };

  const renderMentorshipButtonAction = () => {
    const handleButtonClick = () => {
      const schema = new DatahubViewCreateMentorshipService();

      DatahubEventsService.sendEvent({ schema });

      showSurvey(SURVEY_ID_LIST.SURVEY_MENTORSHIP_EXPERIMENT);
    };

    return (
      <>
        {isEnabledMentorship && (
          <button
            className="_d-flex _px-5 _py-4 _text-left"
            onClick={handleButtonClick}
          >
            <div className="_d-flex _align-items-center">
              <FontAwesomeIcon icon={faHandshakeAngle} />
              <p className="_mb-0 _mt-0 _ml-4 _text-2 _text-gray-700">
                <I18n label="mentorship.session" />
                <Badge className="_font-weight-bold _ml-3">
                  <I18n label="general.beta" />
                </Badge>
              </p>
            </div>
          </button>
        )}
      </>
    );
  };

  return (
    <>
      <Dropdown.MenuItem className="_p-0 _w-full">
        <Link
          to={contentNewURL({
            itemId,
            itemType: MODULE_CONTENT,
            isExtra,
            selectedSeason
          })}
          onClick={() => {
            ContentService.lastCreatedModule = null;
            ContentService.lastEditedModule = null;

            ContentEvents.sendModuleEvent({ label: 'Content' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                category: EVENTS_CATEGORY.CONTENT.MANAGE_MODULE.NAME,
                label: EVENTS_LABELS.CONTENT.MANAGE_MODULE.CREATE_CONTENT_PAGE,
                action: EVENTS_INTERACTIONS.CLICK
              })
            });
          }}
          className="draggable-link"
        >
          {renderContenButtonAction(
            <div className="_d-flex _align-items-center _px-5 _py-4">
              <FileAlt className="content-create-actions__icon" />
              <p className="_mb-0 _mt-0 _ml-4 _text-2 _text-gray-700">
                <I18n label="general.content" />
              </p>
            </div>
          )}
        </Link>
      </Dropdown.MenuItem>

      <Dropdown.MenuItem className="_p-0">
        {renderWebinarButtonAction()}
      </Dropdown.MenuItem>

      <DisabledOnOnlineEvent>
        <Dropdown.MenuItem className="_p-0 _w-full">
          <Link
            to={contentNewURL({
              itemId,
              itemType: MODULE_QUIZ,
              isExtra,
              selectedSeason
            })}
            onClick={() =>
              onCreateContentEvents(EVENTS_LABELS.CONTENT.MANAGE_MODULE.QUIZ)
            }
            className="draggable-link"
          >
            <div className="_d-flex _align-items-center _px-5 _py-4">
              <QuestionSquare className="content-create-actions__icon" />
              <p className="_mb-0 _mt-0 _ml-4 _text-2 _text-gray-700">
                <I18n label="general.quiz" />
              </p>
            </div>
          </Link>
        </Dropdown.MenuItem>
      </DisabledOnOnlineEvent>

      {isMobile ? (
        <Dropdown.MenuItem className="_p-0 _w-full">
          <Link
            to={contentNewURL({
              itemId,
              isExtra,
              selectedSeason,
              itemType: MODULE_ADVERTISEMENT
            })}
            className="draggable-link"
            onClick={handleAdsButtonClick}
          >
            <div className="_d-flex _align-items-center _px-5 _py-4">
              <BadgeDollar className="content-create-actions__icon" />
              <p className="_mb-0 _mt-0 _ml-4 _text-2 _text-gray-700">
                <I18n label="general.upsell" />
              </p>
            </div>
          </Link>
        </Dropdown.MenuItem>
      ) : null}

      <Dropdown.MenuItem className="_p-0">
        {renderMentorshipButtonAction()}
      </Dropdown.MenuItem>
    </>
  );
};

ContentCreateActions.propTypes = {
  history: PropTypes.object,
  isExtra: PropTypes.bool,
  isOnlineEvent: PropTypes.bool,
  itemId: PropTypes.string,
  onboarding: MobxPropTypes.objectOrObservableObject,
  selectedSeason: PropTypes.string,
  urlWithParams: PropTypes.func,
  liveMfeToggle: MobxPropTypes.objectOrObservableObject
};

export default inject((stores) => ({
  isMobile: stores.UiStore.isMobile,
  isOnlineEvent: stores.MembershipStore.onlineEvent,
  onboarding: stores.ContentOnboardingStore,
  liveMfeToggle: stores.LiveStore.mfeToggle,
  isEnabledMentorship: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.EnableMentorshipExperiment
  )
}))(withRouter(ContentCreateActions));
